import React, { useRef, useState } from 'react'
import { dateTimeText, utcText } from '../../../shared/date/local-time'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import { lmsAdmin } from '../api'
import { LmsIcon } from '../lms-icon'
import { ResendDialog } from '../resend-dialog'

// The xAPI statements a connection's record store did not take (the diagnostics'
// failed_statements { count, oldest_failed_at }), with "Resend failed statements"
// for the LMS admin scope. Drawn only when some failed. `reload` reads the
// diagnostics again after a resend.
export const FailedStatementsSection = ({ connectionId, failed, archived, reload }) => {
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const isLmsAdmin = !!lms.accessCreate && !!lms.accessDelete
  const [open, updateOpen] = useState(false)
  const sent = useRef(false)
  if (!failed || !(failed.count > 0)) return null
  const note = archived ? 'An archived connection sends no statements.' : !isLmsAdmin ? 'Resending needs the LMS administrator role.' : null
  return (
    <div className="form-section" data-testid="failed-statements">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="triangle-exclamation" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Failed xAPI statements</h2>
      </div>
      <div className="field">
        <div data-testid="failed-statements-count">
          {failed.count} xAPI {failed.count === 1 ? 'statement' : 'statements'} could not be sent to the record store.
        </div>
        {failed.oldest_failed_at && (
          <div className="field-help" style={{ marginTop: 0 }} data-testid="failed-statements-oldest">
            {'Oldest failed '}
            <span title={utcText(failed.oldest_failed_at)}>{dateTimeText(failed.oldest_failed_at)}</span>
          </div>
        )}
        <button
          className="btn btn-outline-secondary btn-sm"
          style={{ marginTop: 8 }}
          type="button"
          disabled={!!note}
          data-testid="xapi-resend"
          onClick={() => updateOpen(true)}
        >
          Resend failed statements
        </button>
        {note && (
          <div className="field-help" data-testid="xapi-resend-note">
            {note}
          </div>
        )}
      </div>
      {open && (
        <ResendDialog
          testId="xapi-resend-dialog"
          title="Resend failed statements"
          intro="The failed statements are queued to be sent to the record store again. The reason is kept in the audit log."
          sendLabel="Resend statements"
          failureFallback="The failed statements couldn't be queued again."
          send={reason => lmsAdmin.resendXapiStatements(connectionId, reason)}
          outcome={result =>
            typeof result.requeued === 'number'
              ? `${result.requeued} ${result.requeued === 1 ? 'statement' : 'statements'} queued to be sent again.`
              : 'The failed statements were queued to be sent again.'
          }
          onDone={() => { sent.current = true }}
          onClose={() => {
            updateOpen(false)
            // The counts are read again once the person has seen what was done.
            if (sent.current) { sent.current = false; reload() }
          }}
        />
      )}
    </div>
  )
}
